import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { COLORS } from '../../../../styles/UISystem';
import { firstNameOf } from '../../../../utils/contactUtils';
import { logFunnelInterstitialShown } from '../../../../services/analytics/eventLogger';

/**
 * AnonThreadInterstitial — THREAD's scan landing (variants block plan 3,
 * Step 4; design `anon_1_initial.png`).
 *
 * D12: this is a WEB card borrowing App Clip design language — NOT a real App
 * Clip, which needs the Stage-4 native app (epic §2). Deliberate. The one
 * caution carried from that ruling: people who have seen a real App Clip card
 * may expect an app to launch, so the action must do something obviously
 * in-page. [Ok] dismisses straight into the thread underneath — no app
 * switch, no store sheet, no waiting.
 *
 * Pure view-gate, exactly like TEXT's interstitial: formation stays mount-fired
 * in AnonExchangeFlow (tap-gated formation is a rails change, not bought here).
 * The mount logs the shown micro-event, so a no-tap is a clean "no interest"
 * signal and shown-count stays the honest denominator.
 *
 * Unlike TEXT's full-screen interstitial this one is a CARD over the thread —
 * the thread is already behind it, which is the variant's whole premise: the
 * connection is visibly real before you are asked for anything.
 */
const AnonThreadInterstitial = ({ chatRoomId, profileData, onDismiss, heroSrc }) => {
  const firstName = firstNameOf(profileData?.name);

  useEffect(() => {
    if (chatRoomId) logFunnelInterstitialShown(chatRoomId);
  }, [chatRoomId]);

  return (
    <div
      style={{
        // FIXED, not absolute-inside-100vh. On iOS Safari `100vh` is the LARGE
        // viewport — it ignores the floating URL bar — so an absolutely
        // positioned child sat underneath the bar and got clipped. A fixed
        // element resolves against the SMALL viewport, which is exactly the
        // area the bar leaves, so the card lands just above it with no magic
        // number and no browser-vs-PWA branch.
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        padding: '0 12px calc(14px + env(safe-area-inset-bottom))',
        // No scrim: the thread behind stays fully legible. The card is an
        // invitation over a visible connection, not a modal blocking it.
        pointerEvents: 'none',
        // Above the composer/gate footer (z-index 10 in ConnectionPageShared).
        // Without this the [Share contact] pill punched through the card —
        // both are bottom-anchored, so they occupy the same band.
        zIndex: 1200,
      }}
    >
      <div
        style={{
          pointerEvents: 'auto',
          borderRadius: '24px',
          overflow: 'hidden',
          boxShadow: '0 10px 34px rgba(0, 0, 0, 0.16)',
        }}
      >
        {/* Hero. A gradient is the SHIPPED V1 treatment (user ruling
            2026-08-15) — anon_1's arc pattern is not in the repo, and rather
            than approximate it with invented decoration we run the mock's
            blue→teal palette clean. `heroSrc` is the door for real artwork
            when it arrives: pass it and the gradient steps aside, no other
            change. */}
        {heroSrc ? (
          <img
            src={heroSrc}
            alt=""
            style={{ display: 'block', width: '100%', height: '172px', objectFit: 'cover' }}
          />
        ) : (
          <div
            style={{
              height: '172px',
              background:
                'linear-gradient(150deg, #4FB8F5 0%, #5AC8FA 38%, #6FD2DE 72%, #8ADCCF 100%)',
            }}
          />
        )}

        {/* Info bar — App Clip's translucent grey slab. Darkened from the
            first pass so WHITE lettering actually reads on it: white on the
            old #AEB2B7 was about 2:1, which is a legibility problem, not a
            taste one. */}
        <div
          style={{
            backgroundColor: '#9AA0A6',
            padding: '16px 18px 14px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Text block and button share a baseline-ish row: the button centres
              against the two-line pair rather than against the whole slab, so
              it reads as belonging to the message. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {/* textAlign LEFT is load-bearing: App.css sets `.App {
                text-align: center }`, which these block elements inherit. The
                crown row below is a flex container, so its items ignore it and
                sit left — which is exactly why the title looked indented
                relative to "Royale". */}
            <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
              <div
                style={{
                  fontSize: '22px',
                  fontWeight: '700',
                  color: COLORS.WHITE,
                  letterSpacing: '-0.4px',
                  lineHeight: 1.15,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {firstName ? `Connect with ${firstName}` : 'Connect'}
              </div>
              <div
                style={{
                  fontSize: '16px',
                  fontWeight: '400',
                  color: 'rgba(255, 255, 255, 0.8)',
                  letterSpacing: '-0.1px',
                  lineHeight: 1.3,
                  marginTop: '3px',
                }}
              >
                Swap contacts or socials
              </div>
            </div>

            {/* Blue pill is App Clip's language, not the app's black primary —
                the borrowed treatment is the point (D12). */}
            <button
              onClick={onDismiss}
              style={{
                flexShrink: 0,
                backgroundColor: COLORS.PRIMARY_BLUE,
                color: COLORS.WHITE,
                border: 'none',
                borderRadius: '999px',
                padding: '0 30px',
                height: '42px',
                fontSize: '18px',
                fontWeight: '600',
                letterSpacing: '-0.2px',
                cursor: 'pointer',
              }}
            >
              Ok
            </button>
          </div>

          {/* Attribution sits on the same left rule as the title — one inset
              governs the whole slab. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '16px' }}>
            <img
              src="/images/royale-crown-white-96.png"
              alt=""
              style={{ width: '22px', height: '14px', objectFit: 'contain', opacity: 0.95 }}
            />
            <span
              style={{
                fontSize: '16px',
                fontWeight: '400',
                color: 'rgba(255, 255, 255, 0.92)',
                letterSpacing: '-0.1px',
              }}
            >
              Royale
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

AnonThreadInterstitial.propTypes = {
  chatRoomId: PropTypes.string,
  profileData: PropTypes.shape({
    name: PropTypes.string,
    photoUrl: PropTypes.string,
  }),
  onDismiss: PropTypes.func.isRequired,
  heroSrc: PropTypes.string,
};

export default AnonThreadInterstitial;
